import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MartTimeSeriesPoint } from 'src/redux/admin/mart-reporting/mart-reporting.types';
import { cn } from 'src/lib/utils';
import { BetaChartCard } from './beta-chart-card';
import {
  BetaChartAreaSkeleton,
  BetaTimeSeriesMetricChipsSkeleton,
} from './beta-chart-skeletons';
import {
  assignDualAxes,
  BETA_TIME_SERIES_METRICS,
  BetaTimeSeriesMetricKey,
  buildProcessedChartData,
  computeScaleMode,
  formatMetricAxisValue,
  formatMetricTooltipValue,
  scaleModeDescription,
} from './beta-time-series-utils';

interface BetaTimeSeriesChartProps {
  data: MartTimeSeriesPoint[];
  subtitle?: string;
  isLoading?: boolean;
}

function TimeSeriesTooltip({
  active,
  payload,
  label,
  scaleMode,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string; payload?: { originalValues?: Record<string, number> } }>;
  label?: string;
  scaleMode: ReturnType<typeof computeScaleMode>;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-[#E8E9ED] bg-white px-3 py-2.5 shadow-[0_4px_16px_rgba(13,15,26,0.10)]">
      {label ? (
        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.4px] text-[#8A8EA0]">
          {label}
        </p>
      ) : null}
      <div className="space-y-1">
        {payload.map((entry) => {
          const metric = BETA_TIME_SERIES_METRICS.find((item) => item.label === entry.name);
          const metricKey = metric?.key;
          const rawValue =
            scaleMode === 'normalized' || scaleMode === 'hidden'
              ? entry.payload?.originalValues?.[metricKey ?? '']
              : entry.value;
          if (rawValue == null || !metricKey) return null;

          return (
            <div key={entry.name} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-[12px] font-semibold text-[#5E6278]">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: entry.color ?? metric.colour }}
                />
                {entry.name}
              </span>
              <span className="text-[12px] font-bold tabular-nums text-[#0D0F1A]">
                {formatMetricTooltipValue(Number(rawValue), metricKey)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function BetaTimeSeriesChart({ data, subtitle, isLoading = false }: BetaTimeSeriesChartProps) {
  const [hiddenMetrics, setHiddenMetrics] = useState<string[]>([]);

  const visibleKeys = useMemo(
    () =>
      BETA_TIME_SERIES_METRICS.map((metric) => metric.key).filter(
        (key) => !hiddenMetrics.includes(key),
      ) as BetaTimeSeriesMetricKey[],
    [hiddenMetrics],
  );

  const scaleMode = useMemo(() => computeScaleMode(visibleKeys, data), [visibleKeys, data]);

  const chartData = useMemo(
    () => buildProcessedChartData(data, visibleKeys, scaleMode),
    [data, visibleKeys, scaleMode],
  );

  const dualAxes = useMemo(
    () => (scaleMode === 'dual' ? assignDualAxes(visibleKeys, data) : null),
    [scaleMode, visibleKeys, data],
  );

  const scaleHint = scaleModeDescription(scaleMode, visibleKeys, data);

  const leftAxisKey =
    scaleMode === 'dual' && dualAxes
      ? dualAxes.leftKey
      : visibleKeys[0] ?? 'clicks';
  const rightAxisKey =
    scaleMode === 'dual' && dualAxes ? dualAxes.rightKey : visibleKeys[1] ?? 'impressions';

  const metricChips = (
    <div className="flex flex-wrap items-center gap-2">
      {BETA_TIME_SERIES_METRICS.map((metric) => {
        const active = !hiddenMetrics.includes(metric.key);
        return (
          <button
            key={metric.key}
            type="button"
            aria-pressed={active}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-full border-[1.5px] px-3 py-1.5 text-[12px] font-semibold transition-all',
              active
                ? 'text-white shadow-[0_1px_3px_rgba(13,15,26,0.12)]'
                : 'border-[#D9DBE3] bg-white text-[#5E6278] hover:border-[#B8BBCA] hover:text-[#0D0F1A]',
            )}
            style={
              active
                ? { backgroundColor: metric.colour, borderColor: metric.colour }
                : undefined
            }
            onClick={() =>
              setHiddenMetrics((current) => {
                const isHidden = current.includes(metric.key);
                if (isHidden) {
                  return current.filter((key) => key !== metric.key);
                }
                if (visibleKeys.length <= 1) {
                  return current;
                }
                return [...current, metric.key];
              })
            }
          >
            <span
              className={cn('h-1.5 w-3 rounded-sm', !active && 'opacity-80')}
              style={{ backgroundColor: active ? 'rgba(255,255,255,0.9)' : metric.colour }}
            />
            {metric.label}
          </button>
        );
      })}
    </div>
  );

  const showLeftAxis = scaleMode !== 'hidden' && visibleKeys.length > 0;
  const showRightAxis = scaleMode === 'dual';
  const showHiddenAxis = scaleMode === 'hidden' && visibleKeys.length > 0;
  const chartMargin = {
    top: 8,
    right: showRightAxis ? 8 : 4,
    left: 4,
    bottom: 0,
  };

  return (
    <BetaChartCard
      title="Multi-Metric Trend"
      subtitle={subtitle}
      controls={isLoading ? <BetaTimeSeriesMetricChipsSkeleton /> : metricChips}
    >
      {isLoading ? (
        <BetaChartAreaSkeleton className="h-[280px]" />
      ) : chartData.length === 0 ? (
        <div className="flex h-[280px] items-center justify-center text-sm text-[#8A8EA0]">
          No time-series data for the selected filters.
        </div>
      ) : visibleKeys.length === 0 ? (
        <div className="flex h-[280px] items-center justify-center text-sm text-[#8A8EA0]">
          Select at least one metric to display.
        </div>
      ) : (
        <>
          {scaleHint ? (
            <p className="mb-3 text-[11px] text-[#8A8EA0]">{scaleHint}</p>
          ) : null}
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={chartMargin}>
                <CartesianGrid stroke="#EDEEF2" vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fill: '#8A8EA0', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                {showHiddenAxis ? (
                  <YAxis yAxisId="left" domain={[0, 100]} hide />
                ) : null}
                {showLeftAxis ? (
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    domain={scaleMode === 'normalized' ? [0, 100] : ['auto', 'auto']}
                    tick={{ fill: '#8A8EA0', fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={52}
                    tickFormatter={(value) =>
                      scaleMode === 'normalized' || scaleMode === 'hidden'
                        ? `${Math.round(value)}%`
                        : formatMetricAxisValue(value, leftAxisKey)
                    }
                  />
                ) : null}
                {showRightAxis ? (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={['auto', 'auto']}
                    tick={{ fill: '#8A8EA0', fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={52}
                    tickFormatter={(value) => formatMetricAxisValue(value, rightAxisKey)}
                  />
                ) : null}
                <Tooltip
                  content={<TimeSeriesTooltip scaleMode={scaleMode} />}
                  cursor={{ stroke: '#D9DBE3', strokeWidth: 1 }}
                />
                <Legend content={() => null} />
                {BETA_TIME_SERIES_METRICS.map((metric) => {
                  if (hiddenMetrics.includes(metric.key)) return null;

                  let yAxisId = 'left';
                  if (scaleMode === 'dual' && dualAxes) {
                    yAxisId = metric.key === dualAxes.rightKey ? 'right' : 'left';
                  } else if (scaleMode === 'hidden') {
                    yAxisId = 'left';
                  }

                  return (
                    <Line
                      key={metric.key}
                      type="monotone"
                      dataKey={metric.key}
                      name={metric.label}
                      stroke={metric.colour}
                      strokeWidth={2}
                      dot={false}
                      yAxisId={yAxisId}
                      isAnimationActive={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </BetaChartCard>
  );
}
